/**
 * FH Logo Slider - Custom Elements
 * ========================================================================
 * 
 * Provides:
 * - fh-logo-marquee: Continuous scrolling marquee with auto-duplication
 * - fh-logo-carousel: Slider with navigation support
 * 
 * @version 1.0.0
 * @author Freudenhaus
 */

/**
 * FH Logo Marquee Component
 * 
 * Creates a seamless infinite scrolling marquee by duplicating content.
 * Supports pause on hover and configurable speed/direction.
 */
class FHLogoMarquee extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.content = null;
    this.isInitialized = false;
  }

  connectedCallback() {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      this.init();
    });
  }

  init() {
    if (this.isInitialized) return;
    
    this.track = this.querySelector('[data-marquee-track]');
    this.content = this.querySelector('[data-marquee-content]');
    
    if (!this.track || !this.content) return;
    
    // Duplicate content for seamless loop
    this.duplicateContent();
    
    // Setup pause on hover if enabled
    if (this.hasAttribute('data-pause-on-hover')) {
      this.setupPauseOnHover();
    }
    
    this.isInitialized = true;
  }

  /**
   * Duplicate the content to create seamless infinite scroll
   */
  duplicateContent() {
    const clone = this.content.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    this.track.appendChild(clone);
  }

  /**
   * Setup pause on hover functionality
   */
  setupPauseOnHover() {
    this.addEventListener('mouseenter', () => {
      this.setAttribute('data-paused', '');
    });
    
    this.addEventListener('mouseleave', () => {
      this.removeAttribute('data-paused');
    });
  }
}

/**
 * FH Logo Carousel Component
 * 
 * Slider with arrow navigation and touch/swipe support.
 */
class FHLogoCarousel extends HTMLElement {
  constructor() {
    super();
    this.viewport = null;
    this.slides = null;
    this.prevButton = null;
    this.nextButton = null;
    this.currentIndex = 0;
    this.itemsPerView = 6;
    this.totalItems = 0;
    this.isInitialized = false;
  }

  connectedCallback() {
    requestAnimationFrame(() => {
      this.init();
    });
  }

  init() {
    if (this.isInitialized) return;
    
    this.viewport = this.querySelector('[data-viewport]');
    this.slides = this.querySelector('[data-slides]');
    this.prevButton = this.querySelector('[data-prev]');
    this.nextButton = this.querySelector('[data-next]');
    
    if (!this.viewport || !this.slides) return;
    
    this.totalItems = this.slides.children.length;
    this.calculateItemsPerView();
    
    // Setup navigation
    if (this.prevButton && this.nextButton) {
      this.setupNavigation();
    }
    
    // Setup resize observer
    this.setupResizeObserver();
    
    // Update button states
    this.updateButtonStates();
    
    this.isInitialized = true;
  }

  /**
   * Calculate how many items are visible based on viewport width
   */
  calculateItemsPerView() {
    if (!this.slides.children.length) return;
    const firstItem = this.slides.children[0];
    const itemWidth = firstItem.offsetWidth;
    if (itemWidth > 0) {
      this.itemsPerView = Math.round(this.viewport.offsetWidth / itemWidth) || 6;
    }
  }

  /**
   * Setup navigation button event listeners
   */
  setupNavigation() {
    this.prevButton.addEventListener('click', () => this.prev());
    this.nextButton.addEventListener('click', () => this.next());
  }

  /**
   * Setup resize observer to recalculate items per view
   */
  setupResizeObserver() {
    const resizeObserver = new ResizeObserver(() => {
      this.calculateItemsPerView();
      this.updateButtonStates();
    });
    resizeObserver.observe(this.viewport);
  }

  /**
   * Navigate to previous set of items
   */
  prev() {
    if (this.currentIndex <= 0) return;
    this.currentIndex = Math.max(0, this.currentIndex - this.itemsPerView);
    this.scrollToIndex();
  }

  /**
   * Navigate to next set of items
   */
  next() {
    const maxIndex = this.totalItems - this.itemsPerView;
    if (this.currentIndex >= maxIndex) return;
    this.currentIndex = Math.min(maxIndex, this.currentIndex + this.itemsPerView);
    this.scrollToIndex();
  }

  /**
   * Scroll to the current index
   */
  scrollToIndex() {
    const items = this.slides.children;
    if (items[this.currentIndex]) {
      const scrollLeft = items[this.currentIndex].offsetLeft;
      this.viewport.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
    this.updateButtonStates();
  }

  /**
   * Update disabled states of navigation buttons
   */
  updateButtonStates() {
    if (!this.prevButton || !this.nextButton) return;
    
    const maxIndex = Math.max(0, this.totalItems - this.itemsPerView);
    
    this.prevButton.disabled = this.currentIndex <= 0;
    this.nextButton.disabled = this.currentIndex >= maxIndex;
  }
}

// Register custom elements
if (!customElements.get('fh-logo-marquee')) {
  customElements.define('fh-logo-marquee', FHLogoMarquee);
}

if (!customElements.get('fh-logo-carousel')) {
  customElements.define('fh-logo-carousel', FHLogoCarousel);
}
